import type { AgentType } from "./types";

// A fresh-browser bootstrap artifact only pays for itself once a session is well
// beyond the browser's normal cheap replay path. 1,600 events intentionally
// lines up with the existing large-Claude replay safety rail, so the first
// rollout only classifies sessions that are already in the range where full
// browser materialization has proven expensive. If traces show smaller sessions
// are also painful, lower this threshold with matching client/server metrics.
export const NEW_SYNC_BOOTSTRAP_LARGE_SESSION_EVENT_COUNT_THRESHOLD = 1_600;

export type NewSyncBootstrapEligibilityReason =
  | "empty_session"
  | "unsupported_agent"
  | "history_continuity_broken"
  | "replication_continuity_broken"
  | "below_large_session_threshold"
  | "large_session";

export type NewSyncBootstrapArtifactUnavailableReason =
  | "not_eligible"
  | "not_implemented"
  | "server_materializer_unavailable"
  | "no_checkpoint_metadata"
  | "stale_checkpoint"
  | "unsupported_checkpoint_version"
  | "unsupported_projection_version"
  | "unsafe_checkpoint_cursor_mapping";

export type NewSyncBootstrapArtifactKind =
  | "live_runtime_checkpoint_v1"
  | "r2_runtime_checkpoint_v1"
  | "partial_server_state_v1";

export const RUNTIME_CHECKPOINT_VERSION_V1 = 1;

// Keep the first rollout deliberately conservative: a 32 MiB decoded checkpoint
// is large enough for today's huge Claude sessions while still bounded for Worker
// request handling and browser JSON parsing. If checkpoint-friendly projections
// make objects much smaller, lower this to catch regressions earlier.
export const RUNTIME_CHECKPOINT_MAX_BYTE_LENGTH = 32 * 1024 * 1024;

// Version the agent-specific projection separately from the outer blob envelope
// so Claude/OpenCode projection semantics can evolve without changing transport.
export const RUNTIME_CHECKPOINT_PROJECTION_VERSION_V1 = 1;

export type RuntimeCheckpointStorageEncoding = "identity" | "gzip";

// Runtime checkpoint cursors can come from two different cursor domains, and
// the validator must compare like with like. Historically the sidecar
// emitted `checkpointCursor = sidecarHeadSeq` (a runtime source-seq value)
// while the DO compared it against browser `raw_events.id`, which caused
// healthy source-seq checkpoints to be rejected as `cursor_ahead_of_tail` on
// large/active threads. Callers should tag uploads with the correct kind; the
// DO validates source-seq checkpoints against the source-seq tail and
// browser-cursor checkpoints against the browser raw-event tail.
//
// `runtime_source_seq` is the default when not specified so existing sidecar
// uploads (which have always been source-seq based in practice) validate
// correctly without requiring a lockstep sidecar upgrade.
export type RuntimeCheckpointCursorKind =
  | "runtime_source_seq"
  | "browser_raw_event_id";

export const DEFAULT_RUNTIME_CHECKPOINT_CURSOR_KIND: RuntimeCheckpointCursorKind =
  "runtime_source_seq";

export interface RuntimeCheckpointMetadataV1 {
  agentType: AgentType;
  // Runtime checkpoints currently represent a single projected conversation.
  // Existing sidecar/R2 uploads are default-conversation only; non-default
  // conversation checkpoints require separate metadata/storage support.
  conversationId?: string | null;
  // Provider-native session represented by this checkpoint. This is especially
  // important for OpenCode, where child-task reconstruction depends on knowing
  // which provider session is the root conversation for the checkpoint.
  providerSessionId?: string | null;
  checkpointCursor: number;
  // Cursor domain for `checkpointCursor`. Optional for backward compatibility
  // with older uploads and stored rows; absent/null is treated as
  // `runtime_source_seq` because that matches the sidecar uploader behavior
  // that has always been in place.
  cursorKind?: RuntimeCheckpointCursorKind;
  r2Key: string;
  // Logical checkpoint byte length/hash for the uncompressed JSON envelope. These
  // remain the browser validation identity even when the physical R2 object is
  // stored with `storageEncoding: "gzip"`.
  byteLength: number;
  sha256: string;
  // Physical R2/blob-response encoding and byte identity. Older metadata rows
  // omit these fields and are interpreted as identity storage.
  storageEncoding?: RuntimeCheckpointStorageEncoding;
  storedByteLength?: number;
  storedSha256?: string;
  checkpointVersion: typeof RUNTIME_CHECKPOINT_VERSION_V1;
  projectionVersion: number;
  browserHistoryEpoch: number;
  journalEpoch: string | null;
  historyToken: string | null;
  createdAtMs: number;
}

export interface RuntimeCheckpointBlobV1 {
  checkpointVersion: typeof RUNTIME_CHECKPOINT_VERSION_V1;
  sessionId: string;
  agentType: AgentType;
  conversationId?: string | null;
  providerSessionId?: string | null;
  checkpointCursor: number;
  browserHistoryEpoch: number;
  journalEpoch: string | null;
  historyToken: string | null;
  projectionVersion: number;
  createdAtMs: number;
  runtimeState: unknown;
}

export interface RuntimeCheckpointUploadRequestV1 {
  sessionId: string;
  agentType: AgentType;
  conversationId?: string | null;
  providerSessionId?: string | null;
  checkpointCursor: number;
  // Optional, see RuntimeCheckpointMetadataV1.cursorKind. Defaults to
  // `runtime_source_seq` when not provided.
  cursorKind?: RuntimeCheckpointCursorKind;
  checkpointVersion: typeof RUNTIME_CHECKPOINT_VERSION_V1;
  projectionVersion: number;
  browserHistoryEpoch: number;
  journalEpoch: string | null;
  historyToken: string | null;
  sha256: string;
  byteLength: number;
  checkpointBase64: string;
}

export interface RuntimeCheckpointUploadResponseV1 {
  ok: true;
  accepted: boolean;
  checkpointCursor: number;
  r2Key: string | null;
  rejectedReason?: string;
}

export interface NewSyncBootstrapEligibilityResponse {
  eligible: boolean;
  artifactAvailable: boolean;
  artifactUnavailableReason: NewSyncBootstrapArtifactUnavailableReason | null;
  reason: NewSyncBootstrapEligibilityReason;
  agentType: AgentType;
  headCursor: number;
  tailId: number;
  rawEventCount: number;
  largeSessionEventThreshold: number;
  historyContinuity: "contiguous" | "broken";
  replicationContinuity: "contiguous" | "broken";
  historyToken: string | null;
  browserHistoryEpoch: number;
  baselineFingerprint: string | null;
  baselineFingerprintStatus: "cached" | "missing";
  olderHistoryBackfillSupported: boolean;
}

export interface NewSyncBootstrapArtifactResponse extends NewSyncBootstrapEligibilityResponse {
  artifactKind: NewSyncBootstrapArtifactKind | null;
  checkpoint: RuntimeCheckpointMetadataV1 | null;
  blobFetchPath: string | null;
  canSeedLiveRuntime: boolean;
  canResumeFromBootstrapCursor: boolean;
  // Browser raw-event cursor from which the client should resume live/catchup
  // after applying the bootstrap artifact. This is intentionally separate from
  // RuntimeCheckpointMetadataV1.checkpointCursor because runtime checkpoints can
  // be keyed by runtime source-seq rather than browser raw-event id.
  bootstrapCursor: number | null;
  olderHistoryDeferred: boolean;
  materializerStatus:
    | "unavailable"
    | "available"
    | "not_requested";
}
